import { zodResolver } from "@hookform/resolvers/zod";
import {
  SkyAccordion,
  SkyAccordionItem,
  SkyCheckbox,
  SkyDialog,
  SkyDrawer,
  SkyDropdown,
  SkyInput,
  SkyMessageBar,
  SkySwitch,
  SkyText,
  type CheckboxState,
} from "@skypointcloud/skypoint-ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { unknownSelectorMethod, unknownSelectorMethodSentence } from "@shared/dbt/selectorMethods";
import {
  MAX_DBT_SELECTOR_LENGTH,
  type CreateDbtJobBody,
  type DbtJobView,
  type UpdateDbtJobBody,
} from "@shared/types/transformations";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import {
  useCreateDbtJob,
  useDeleteDbtJob,
  useTransformationGraph,
  useUpdateDbtJob,
} from "@/hooks/use-dbt-jobs";
import { ApiClientError, failureMessage } from "@/lib/api";
import { firstDisallowedSelectorCharacter } from "./transformFormatting";

/**
 * One drawer for create and edit (canvas 15 block 03; FR-62).
 *
 * react-hook-form with a zod resolver, and the zod rules are the SERVER's own:
 * the name bounds come from `createDbtJobBodySchema` and the selector
 * charset is `DBT_SELECTOR_CHARSET` from `shared/types/transformations.ts`, so a
 * typo is refused in the drawer rather than as exit class 14 at 02:00. The
 * selector error names the character, because "`;` is not allowed" tells the
 * user which key they hit and the rule alone does not.
 *
 * The depends-on picker never lists the row itself, and a longer cycle is the
 * server's to refuse (400, both names in the message): the client cannot know
 * the graph it is about to join better than the database does, so the 400 is
 * shown inline where the picker is rather than as a toast that vanishes.
 *
 * Mounted only while open, so the fields start from the row every time.
 *
 * Round 6 T-29 (owner 2026-10-02, the tag standard): a dbt job is a saved, named dbt selection,
 * and the usual one is a tag. The Tag list offers the tags of the repository's last manifest
 * (`graph.tags`, from the data the Models view already reads; no run is started for it). A tag
 * sets the selector to `tag:<name>` and, while Name is empty or still the previous tag's name,
 * names the job after it. The free-text selector stays, under "Advanced selector", with the
 * same rules; it starts open when there is no tag to offer or the selector is not one tag.
 */

/** `tag:<name>` when the selector is exactly one tag the list offers, else null. */
function tagOf(selector: string, tags: readonly string[]): string | null {
  const match = /^tag:(\S+)$/.exec(selector.trim());
  return match && tags.includes(match[1]!) ? match[1]! : null;
}

const ADVANCED = "advanced";

/** The longest name a dbt job takes; a tag names a job with at most this many characters. */
const MAX_NAME_LENGTH = 200;

export const SELECTOR_HINT = `dbt node selector: tag:marts, +daily_revenue, path:models/staging. Letters, digits, space and _ . , : + * @ / - only, up to ${MAX_DBT_SELECTOR_LENGTH} characters. Left empty, this dbt job is left out of the run: a run is never widened to the whole project.`;

const formSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Name this dbt job.")
    .max(MAX_NAME_LENGTH, `A name is at most ${MAX_NAME_LENGTH} characters.`),
  dbtSelector: z
    .string()
    .max(MAX_DBT_SELECTOR_LENGTH, `A selector is at most ${MAX_DBT_SELECTOR_LENGTH} characters.`)
    .superRefine((value, ctx) => {
      const character = firstDisallowedSelectorCharacter(value);
      if (character !== null) {
        ctx.addIssue({
          code: "custom",
          message: `“${character}” is not allowed in a selector. Use a space for union and a comma for intersection.`,
        });
        return;
      }
      // #1292: the word before a colon must be a method dbt has. The server refuses it too.
      const method = unknownSelectorMethod(value);
      if (method !== null) {
        ctx.addIssue({ code: "custom", message: unknownSelectorMethodSentence(method) });
      }
    }),
  enabled: z.boolean(),
  runTests: z.boolean(),
  dependsOn: z.array(z.string()),
});

type FormValues = z.infer<typeof formSchema>;

function valuesFrom(transformation: DbtJobView | null): FormValues {
  return {
    name: transformation?.name ?? "",
    dbtSelector: transformation?.dbtSelector ?? "",
    enabled: transformation?.enabled ?? true,
    runTests: transformation?.runTests ?? false,
    dependsOn: transformation?.dependsOn ?? [],
  };
}

/** The create body: every field, with an empty selector stored as null. */
function createBodyFrom(values: FormValues): CreateDbtJobBody {
  return {
    name: values.name,
    dbtSelector: values.dbtSelector.trim() === "" ? null : values.dbtSelector.trim(),
    dependsOn: values.dependsOn,
    enabled: values.enabled,
    runTests: values.runTests,
  };
}

/** The update body: only what changed, or null when nothing did. */
function updateBodyFrom(values: FormValues, before: DbtJobView): UpdateDbtJobBody | null {
  const body: UpdateDbtJobBody = {};
  const selector = values.dbtSelector.trim() === "" ? null : values.dbtSelector.trim();
  if (values.name !== before.name) body.name = values.name;
  if (selector !== before.dbtSelector) body.dbtSelector = selector;
  if (values.enabled !== before.enabled) body.enabled = values.enabled;
  if (values.runTests !== before.runTests) body.runTests = values.runTests;
  const sameDependencies =
    values.dependsOn.length === before.dependsOn.length &&
    values.dependsOn.every((id) => before.dependsOn.includes(id));
  if (!sameDependencies) body.dependsOn = values.dependsOn;
  return Object.keys(body).length > 0 ? body : null;
}

export interface DbtJobDrawerProps {
  /** Null creates; a row edits it. */
  transformation: DbtJobView | null;
  /** Every dbt job in the instance, for the depends-on picker. */
  others: DbtJobView[];
  onClose: () => void;
  onSaved?: (view: DbtJobView) => void;
  onDeleted?: (id: string) => void;
  /**
   * The page `onSaved` and `onDeleted` navigate to, if they do. The confirmation is posted for
   * that page (`forPath`), so the route change does not clear it (ux-s10 §3).
   */
  confirmForPath?: string;
}

/**
 * Whether the advanced selector starts open: with no tag to offer, or a selector other than one
 * of them. While the tags load it goes by the selector's shape alone.
 */
function advancedStartsOpen(
  selector: string,
  tags: readonly string[],
  chosenTag: string | null,
  tagsLoading: boolean,
): boolean {
  const typed = selector.trim() !== "";
  if (tagsLoading) return typed && !/^tag:\S+$/.test(selector.trim());
  return tags.length === 0 || (typed && chosenTag === null);
}

/** Round 6 T-29: the manifest's tags to choose from, or that there are none yet once read. */
function TagChoice({
  tags,
  chosenTag,
  tagsRead,
  onChoose,
}: {
  tags: readonly string[];
  chosenTag: string | null;
  tagsRead: boolean;
  onChoose: (tag: string) => void;
}) {
  if (tags.length > 0) {
    return (
      <SkyDropdown
        label="Tag"
        placeholder="Choose a tag"
        options={tags.map((tag) => ({ value: tag, text: tag }))}
        selectedOptions={chosenTag ? [chosenTag] : []}
        onOptionSelect={(values) => {
          if (values[0]) onChoose(values[0]);
        }}
        listboxMaxHeight="320px"
        testId="transformation-tag"
      />
    );
  }
  if (!tagsRead) return null;
  return (
    <SkyText as="p" size={200} color="muted" testId="transformation-tags-none">
      No tags to choose from yet. The list comes from the models the last run listed. Run all dbt
      jobs once, or use Advanced selector.
    </SkyText>
  );
}

export function DbtJobDrawer({
  transformation,
  others,
  onClose,
  onSaved,
  onDeleted,
  confirmForPath,
}: DbtJobDrawerProps) {
  const notice = useNotice();
  // Session 10 (ux-s10 §3): the drawer stays open on a failure, so every failure is said inside
  // it (the notice would sit behind the drawer). One bar, titled by what failed.
  const failure = useInlineError();
  const [failureTitle, setFailureTitle] = useState("The dbt job could not be saved");
  const create = useCreateDbtJob();
  const update = useUpdateDbtJob();
  const remove = useDeleteDbtJob();
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isEdit = transformation !== null;

  // Walk of 9a9d586 (2026-10-02, D-a and D-g): a field is checked as it is typed and on Create,
  // never on blur. Leaving the empty Name for the Tag list replaced the Name's two-line hint with
  // its one-line error between the press and the release, so the Tag control moved up from under
  // the pointer and the click never reached it: the list "never opened".
  const { control, handleSubmit, formState, setValue, getValues, watch, setError } =
    useForm<FormValues>({
      resolver: zodResolver(formSchema),
      defaultValues: valuesFrom(transformation),
      mode: "onChange",
      reValidateMode: "onChange",
    });

  // Focus lands on Name. `SkyInput` exposes no ref, so the field is found by
  // its test id inside the drawer's own subtree.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      bodyRef.current
        ?.querySelector<HTMLInputElement>('[data-testid="transformation-name"] input')
        ?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  // Round 6 T-29: the tags of the last manifest, and the name a tag last gave the job.
  const graph = useTransformationGraph();
  const tags = useMemo(() => graph.data?.tags ?? [], [graph.data?.tags]);
  const selector = watch("dbtSelector");
  const chosenTag = tagOf(selector, tags);
  const autoName = useRef<string | null>(null);
  // Null until the person opens or closes it: then it starts open when there is no tag to offer
  // or the selector is something other than one of them. While the tags load (review r1 nit) it
  // goes by the selector's shape alone, so a one-tag selector does not open and then close.
  const [advancedChoice, setAdvancedChoice] = useState<boolean | null>(null);
  const advancedOpen =
    advancedChoice ?? advancedStartsOpen(selector, tags, chosenTag, graph.isLoading);
  const chooseTag = (tag: string): void => {
    setValue("dbtSelector", `tag:${tag}`, { shouldDirty: true, shouldValidate: true });
    const name = getValues("name");
    if (name.trim() === "" || name === autoName.current) {
      // A tag may be longer than a name takes (review r1 nit): never a Name error for text the
      // person did not type.
      const auto = tag.slice(0, MAX_NAME_LENGTH);
      setValue("name", auto, { shouldDirty: true, shouldValidate: true });
      autoName.current = auto;
    }
  };

  const candidates = useMemo(
    () => others.filter((other) => other.id !== transformation?.id),
    [others, transformation?.id],
  );

  const isSaving = create.isPending || update.isPending;

  const submit = handleSubmit(async (values) => {
    setServerError(null);
    failure.clear();
    try {
      let saved: { view: DbtJobView; title: string } | null = null;
      if (transformation) {
        const body = updateBodyFrom(values, transformation);
        if (!body) {
          onClose();
          return;
        }
        const view = await update.mutateAsync({ id: transformation.id, body });
        onSaved?.(view);
        saved = { view, title: "dbt job saved" };
      } else {
        const view = await create.mutateAsync(createBodyFrom(values));
        onSaved?.(view);
        saved = { view, title: "dbt job created" };
      }
      onClose();
      // After the close, so the notice is posted outside the drawer's hidden window. #1292: a
      // selector that picks no model saves, and the notice says so and stays until dismissed.
      const warning = saved.view.warnings?.[0];
      if (warning) {
        notice.warning(warning.message, { title: saved.title, forPath: confirmForPath });
      } else {
        notice.success(saved.view.name, { title: saved.title, forPath: confirmForPath });
      }
    } catch (error) {
      // A 400 here is the cycle and a 409 the name clash (`ConflictError`
      // from the service): both sentences name the rows, so they stay where
      // the fields are rather than in a toast that vanishes.
      if (error instanceof ApiClientError && (error.status === 400 || error.status === 409)) {
        // #1292: a refused selector is said at the selector field, which opens to show it.
        const selectorIssue = error.details.find((detail) => detail.field === "dbtSelector");
        if (selectorIssue?.issue) {
          setError("dbtSelector", { type: "server", message: selectorIssue.issue });
          setAdvancedChoice(true);
          return;
        }
        setServerError(failureMessage(error, "The dbt job could not be saved."));
        return;
      }
      setFailureTitle("The dbt job could not be saved");
      failure.show(error, "The dbt job could not be saved.");
    }
  });

  const doDelete = async (): Promise<void> => {
    if (!transformation) return;
    setServerError(null);
    failure.clear();
    try {
      await remove.mutateAsync(transformation.id);
      setConfirmDelete(false);
      onDeleted?.(transformation.id);
      onClose();
      notice.success(transformation.name, {
        title: "dbt job deleted",
        forPath: confirmForPath,
      });
    } catch (error) {
      // The confirmation closes; the drawer, which holds Delete, stays and says why.
      setConfirmDelete(false);
      setFailureTitle("The dbt job could not be deleted");
      failure.show(error, "The dbt job could not be deleted.");
    }
  };

  return (
    <>
      <SkyDrawer
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        position="end"
        size="medium"
        testId="transformation-drawer"
        headerTitle={isEdit ? "Edit dbt job" : "New dbt job"}
        subtitle={
          isEdit
            ? transformation.name
            : "Adds a dbt job to this instance. It joins the next run in dependency order."
        }
        footerButtons={[
          ...(isEdit
            ? [
                {
                  label: "Delete",
                  appearance: "subtle" as const,
                  onClick: () => setConfirmDelete(true),
                  disabled: isSaving,
                },
              ]
            : []),
          { label: "Cancel", appearance: "subtle" as const, onClick: onClose, disabled: isSaving },
          {
            label: isEdit ? "Save" : "Create",
            appearance: "primary" as const,
            onClick: () => void submit(),
            loading: isSaving,
            disabled: isSaving,
          },
        ]}
      >
        <div ref={bodyRef}>
          <form
            className="flex flex-col gap-4"
            data-testid="transformation-form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <SkyInput
                  label="Name"
                  required
                  value={field.value}
                  onChange={(event) => field.onChange(event.target.value)}
                  onBlur={field.onBlur}
                  hint={`Up to ${MAX_NAME_LENGTH} characters. Shown in the schedule editor and the run's step list.`}
                  validationState={fieldState.error ? "error" : "none"}
                  validationMessage={fieldState.error?.message}
                  placeholder="Name this dbt job"
                  testId="transformation-name"
                />
              )}
            />

            <TagChoice
              tags={tags}
              chosenTag={chosenTag}
              tagsRead={graph.isSuccess}
              onChoose={chooseTag}
            />

            <SkyAccordion
              size="small"
              openItems={advancedOpen ? [ADVANCED] : []}
              onToggle={(_value, open) => setAdvancedChoice(open.includes(ADVANCED))}
              testId="transformation-advanced"
            >
              <SkyAccordionItem value={ADVANCED} heading="Advanced selector">
                <Controller
                  control={control}
                  name="dbtSelector"
                  render={({ field, fieldState }) => (
                    <SkyInput
                      label="dbt selector"
                      value={field.value}
                      onChange={(event) => field.onChange(event.target.value)}
                      onBlur={field.onBlur}
                      hint={SELECTOR_HINT}
                      validationState={fieldState.error ? "error" : "none"}
                      validationMessage={fieldState.error?.message}
                      placeholder="tag:marts"
                      testId="transformation-selector"
                    />
                  )}
                />
              </SkyAccordionItem>
            </SkyAccordion>

            {/* Round 7 T-36: one switch per row; side by side, Run tests' note wrapped to a
                narrow column beside Enabled's. */}
            <div className="flex flex-col gap-4">
              <Controller
                control={control}
                name="enabled"
                render={({ field }) => (
                  <SkySwitch
                    label="Enabled"
                    description="Off keeps the row and drops it from the bundle."
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    testId="transformation-enabled"
                  />
                )}
              />
              <Controller
                control={control}
                name="runTests"
                render={({ field }) => (
                  <SkySwitch
                    label="Run tests"
                    description="dbt test after dbt run for this selector. Warehouse time in your warehouse account, so off by default."
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    testId="transformation-run-tests"
                  />
                )}
              />
            </div>

            <Controller
              control={control}
              name="dependsOn"
              render={({ field }) => (
                <div>
                  {candidates.length === 0 ? (
                    <>
                      <SkyText as="p" size={300} weight="medium" block>
                        Depends on
                      </SkyText>
                      <SkyText as="p" size={200} color="muted" block>
                        No other dbt job in this instance yet.
                      </SkyText>
                    </>
                  ) : (
                    <SkyCheckbox
                      label="Depends on"
                      layout="vertical"
                      options={candidates.map((candidate) => ({
                        id: candidate.id,
                        label: candidate.name,
                        description: candidate.dbtSelector ?? "(no selector: left out of runs)",
                      }))}
                      value={field.value}
                      onChange={(states: CheckboxState[] | boolean) => {
                        if (typeof states === "boolean") return;
                        field.onChange(states.filter((s) => s.checked).map((s) => s.id));
                      }}
                      testId="transformation-depends-on"
                    />
                  )}
                  <SkyText as="p" size={200} color="muted" className="mt-1">
                    The dbt jobs that must finish first. The row itself is never listed, and a
                    longer cycle is refused on save.
                  </SkyText>
                </div>
              )}
            />

            {serverError ? (
              <SkyMessageBar
                intent="error"
                layout="multiline"
                title="The dbt job could not be saved"
                message={serverError}
                testId="transformation-server-error"
              />
            ) : null}

            <InlineErrorBar
              error={failure.error}
              title={failure.error?.retryable ? undefined : failureTitle}
              testId="transformation-failure"
            />

            {formState.submitCount > 0 && !formState.isValid ? (
              <SkyText size={200} color="danger" role="alert" testId="transformation-form-invalid">
                Fix the fields marked above, then save again.
              </SkyText>
            ) : null}
          </form>
        </div>
      </SkyDrawer>

      {confirmDelete && transformation ? (
        <SkyDialog
          open
          onOpenChange={(open) => {
            if (!open) setConfirmDelete(false);
          }}
          title={`Delete ${transformation.name}?`}
          description="Schedules that name it drop it. Runs already recorded keep their history."
          size="small"
          testId="transformation-delete-dialog"
          primaryAction={{
            label: "Delete",
            destructive: true,
            // Returned, not voided: `SkyDialog` awaits the primary action before
            // it closes itself, so the dialog stays until the DELETE has landed.
            onClick: doDelete,
            loading: remove.isPending,
          }}
          secondaryAction={{ label: "Keep it", onClick: () => setConfirmDelete(false) }}
        />
      ) : null}
    </>
  );
}
